import { CreativeOSPages } from '@/_shared/subscriptionPlans'
import AccessControl from '@/components/AccessControl'
import { Stack } from '@/components/custom-components'
import { Badge } from '@/components/shadcn/badge.tsx'
import { useSidebar } from '@/components/shadcn/sidebar'
import { featureFlagKeys } from '@/utils/data/featureFlags'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useFeatureFlagEnabled } from 'posthog-js/react'

type SearchParams = {
  sideBarOpen?: boolean
}

export const Route = createFileRoute('/feeds/creative-toolkit/')({
  component: All,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const sideBarOpen = search?.sideBarOpen as boolean

    return {
      sideBarOpen,
    }
  },
})

function All() {
  const navigate = useNavigate()
  const { open: sidebarOpen } = useSidebar()

  const creativeAnalysisFeatureFlagEnabled = useFeatureFlagEnabled(featureFlagKeys.creativeAnalysis)

  return (
    <AccessControl page={CreativeOSPages.CREATIVE_TOOLKIT}>
      <Stack className="gap-3 lg:gap-6">
        <Stack className="gap-3 lg:gap-8">
          <Stack className="gap-3 lg:gap-6">
            <div className={'flex justify-between lg:justify-start gap-5 items-center'}>
              <div className={''}>
                <h1 className={'text-2xl font-semibold'}>
                  {sidebarOpen ? 'Your Imagination. Our Tools.' : 'Creative Toolkit: Your Imagination. Our Tools.'}
                </h1>
                <p className={'text-sm lg:text-base text-thememutedforeground mt-2'}>
                  Access powerful features to refine, optimize, and elevate every creation.
                </p>
              </div>
            </div>
          </Stack>
        </Stack>
        <div className="pb-5">
          <div className={'grid lg:grid-cols-3 gap-4'}>
            <div
              style={{
                backgroundImage: `url(/dashboard/t1.jpg)`,
              }}
              className={
                'cursor-pointer hover:border-themedestructive border-2 rounded-lg py-6 px-4 min-h-[20rem] lg:min-h-[20rem] bg-center bg-no-repeat bg-cover'
              }
              onClick={() => navigate({ to: '/feeds/creative-toolkit/copy-grader' })}
            >
              <div>
                <div className={'flex gap-2 justify-between'}>
                  <p className={'text-xl font-medium  text-themeprimary'}>Copy Grader</p>
                  <Badge variant={'secondaryDestructive'}>New</Badge>
                </div>
                <p className={'mt-1.5 leading-relaxed text-themeprimary'}>
                  See the readability score of your copy based on the Flesch–Kincaid Grade Level.
                </p>
              </div>
            </div>
            <div
              style={{
                backgroundImage: `url(/dashboard/t2.jpg)`,
              }}
              className={
                'cursor-pointer hover:border-themedestructive border-2 rounded-lg py-6 px-4 min-h-[20rem] lg:min-h-[20rem] bg-center bg-no-repeat bg-cover'
              }
              onClick={() => navigate({ to: '/feeds/creative-toolkit/ad-test-calculator' })}
            >
              <div>
                <div className={'flex gap-2 justify-between'}>
                  <p className={'text-xl font-medium text-themeprimary'}>Ad-Test Calculator</p>
                  <Badge variant={'secondaryDestructive'}>New</Badge>
                </div>
                <p className={'mt-1.5 leading-relaxed text-themeprimary'}>
                  Calculate the optimal creative testing volume and budget allocation.
                </p>
              </div>
            </div>
            {creativeAnalysisFeatureFlagEnabled && (
              <div
                style={{
                  backgroundImage: `url(/dashboard/t3.jpg)`,
                }}
                className={
                  'cursor-pointer hover:border-themedestructive border-2 rounded-lg py-6 px-4 min-h-[20rem] lg:min-h-[20rem] bg-bottom bg-cover bg-no-repeat'
                }
                onClick={() => navigate({ to: '/feeds/creative-toolkit/creative-analysis' })}
              >
                <div>
                  <div className={'flex gap-2 justify-between'}>
                    <p className={'text-xl font-medium text-themeprimary'}>Creative Analysis</p>
                    <Badge variant={'secondaryDestructive'}>Beta</Badge>
                  </div>
                  <p className={'mt-1.5 leading-relaxed'}>
                    Get AI-powered analysis of what’s working and not working for your creative assets.{' '}
                  </p>
                </div>
              </div>
            )}
            <div
              style={{
                backgroundImage: creativeAnalysisFeatureFlagEnabled
                  ? 'url(/dashboard/t4.jpg)'
                  : 'url(/dashboard/t3.jpg)',
              }}
              className={
                'cursor-pointer hover:border-themedestructive border-2 rounded-lg py-6 px-4 min-h-[20rem] lg:min-h-[20rem] bg-bottom bg-cover bg-no-repeat'
              }
              onClick={() => navigate({ to: '/feeds/creative-toolkit/funnel-creative-analyzer' })}
            >
              <div>
                <div className={'flex gap-2 justify-between'}>
                  <p className={'text-xl font-medium text-themeprimary'}>Funnel Congruence Analysis</p>
                  <Badge variant={'secondaryDestructive'}>Beta</Badge>
                </div>
                <p className={'mt-1.5 leading-relaxed'}>
                  Get AI-powered analysis for your Ad to Landing Page journey and valuable recommendations to improve.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Stack>
    </AccessControl>
  )
}
